import React from 'react';
import { Typography, DefaultButton, makeStyles, Theme } from '@bb-ui/react-library';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { ChevronLeft } from '@bb-ui/icons/dist/small/ChevronLeft';
import { ChevronRight } from '@bb-ui/icons/dist/small/ChevronRight';
import { useTranslation } from 'react-i18next';
import { Skeleton } from './Skeleton';

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showAddSites?: boolean;
  onAddSites?: () => void;
  isLocked?: boolean;
  isDisabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  paginationControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  paginationSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  disabledControl: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '& button': {
      pointerEvents: 'none',
      opacity: 0.6,
    },
  },
}));

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showAddSites = false,
  onAddSites,
  isLocked = false,
  isDisabled = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handlePreviousPage = () => {
    if (!isDisabled) {
      onPageChange(Math.max(currentPage - 1, 0));
    }
  };

  const handleNextPage = () => {
    if (!isDisabled) {
      onPageChange(Math.min(currentPage + 1, totalPages - 1));
    }
  };

  const handleAddSites = () => {
    if (!isDisabled && onAddSites) {
      onAddSites();
    }
  };

  const renderAddSitesSection = () => {
    const shouldShowAddSites = showAddSites && !isLocked;

    if (!shouldShowAddSites || !onAddSites) {
      return null;
    }

    if (isDisabled) {
      return <Skeleton variant="button" width="80px" />;
    }

    const isAddSitesDisabled = isDisabled || isLocked;
    return (
      <DefaultButton onClick={handleAddSites} disabled={isAddSitesDisabled}>
        {t('cohorts.addSites')}
      </DefaultButton>
    );
  };

  const renderPaginationSection = () => {
    if (isDisabled) {
      return (
        <div className={classes.paginationSection}>
          <IconButton aria-label="Previous page" disabled>
            <ChevronLeft style={{ opacity: 0.3 }} />
          </IconButton>
          <Skeleton variant="text" width="60px" />
          <IconButton aria-label="Next page" disabled>
            <ChevronRight style={{ opacity: 0.3 }} />
          </IconButton>
        </div>
      );
    }

    return (
      <div className={classes.paginationSection}>
        <IconButton
          aria-label="Previous page"
          disabled={totalPages <= 1 || currentPage === 0 || isDisabled}
          onClick={handlePreviousPage}
        >
          <ChevronLeft />
        </IconButton>
        <Typography>{totalPages > 0 ? `${currentPage + 1} / ${totalPages}` : '0 / 0'}</Typography>
        <IconButton
          aria-label="Next page"
          disabled={totalPages <= 1 || currentPage === totalPages - 1 || isDisabled}
          onClick={handleNextPage}
        >
          <ChevronRight />
        </IconButton>
      </div>
    );
  };

  return (
    <div className={`${classes.paginationControls} ${isDisabled ? classes.disabledControl : ''}`}>
      <div>{renderAddSitesSection()}</div>
      <div>{renderPaginationSection()}</div>
    </div>
  );
};

export default PaginationControls;
